import React, { useState, useEffect, useContext } from 'react';
import { Download, Upload, Trash2, Database, CheckCircle, RefreshCw, Search, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog.jsx';
import { Button } from './ui/button.jsx';
import { AuthContext } from '../App';

const GestionBackupsFull = () => {
  // Use AuthContext API for proper backend URL handling
  const { API } = useContext(AuthContext);
  
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [cleaningMessages, setCleaningMessages] = useState(false);
  const [selectedBackups, setSelectedBackups] = useState([]);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [diagnosticando, setDiagnosticando] = useState(false);
  const [reseteandoContadores, setReseteandoContadores] = useState(false);
  const [diagnosticoResult, setDiagnosticoResult] = useState(null);
  const [backupColecciones, setBackupColecciones] = useState(false);
  const [showColeccionesConfig, setShowColeccionesConfig] = useState(false);
  const [coleccionesInfo, setColeccionesInfo] = useState([]);
  const [loadingColecciones, setLoadingColecciones] = useState(false);

  // 1. Exportar backup completo (streaming)
  const handleExportComplete = async () => {
    try {
      setExporting(true);
      toast.info('Descargando backup completo... esto puede tomar varios minutos, no cierre la página.');
      
      const response = await fetch(`${API}/admin/backup/export-complete`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();

        // Detección de backup incompleto: el backend marca errores a mitad de
        // stream con el campo "__BACKUP_ERROR__". Validamos antes de bajar para
        // que el admin no se quede con un archivo truncado pensando que está OK.
        const text = await blob.text();
        if (text.includes('"__BACKUP_ERROR__"')) {
          const errMatch = text.match(/"__BACKUP_ERROR__"\s*:\s*"([^"]+)"/);
          const errMsg = errMatch ? errMatch[1] : 'error desconocido durante el streaming';
          toast.error(`❌ Backup INCOMPLETO — no lo uses. Error: ${errMsg}. Intentá de nuevo.`);
          return;
        }

        // Validación de tamaño mínimo: si el archivo es sospechosamente chico
        // (<3 MB) probablemente algo falló silenciosamente. Avisamos.
        const sizeMB = blob.size / (1024 * 1024);
        if (sizeMB < 3) {
          if (!window.confirm(
            `⚠️ El backup pesa solo ${sizeMB.toFixed(2)} MB, lo que es sospechosamente chico (suele pesar 30-50 MB). ` +
            `Puede estar incompleto. ¿Descargarlo igual?`
          )) {
            return;
          }
        }

        // Crear blob descargable a partir del texto validado
        const downloadBlob = new Blob([text], { type: 'application/json' });
        const url = window.URL.createObjectURL(downloadBlob);
        const a = document.createElement('a');
        a.href = url;

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'backup_completo.json';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/);
          if (match) filename = match[1];
        }

        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success(`✅ Backup descargado (${sizeMB.toFixed(1)} MB)`);
      } else {
        const errorText = await response.text().catch(() => 'Error desconocido');
        toast.error(`Error al exportar backup: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al exportar backup. Intente nuevamente.');
    } finally {
      setExporting(false);
    }
  };

  // Función para limpiar mensajes huérfanos
  const limpiarMensajesHuerfanos = async () => {
    if (!window.confirm('¿Limpiar mensajes huérfanos del sistema viejo? Esto NO afectará el chat actual.')) {
      return;
    }

    try {
      setCleaningMessages(true);
      toast.info('🧹 Limpiando mensajes huérfanos...');

      const response = await axios.post(`${API}/admin/limpiar-mensajes-huerfanos`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        toast.success(`✅ ${response.data.message}\n📊 ${response.data.mensajes_eliminados} mensajes eliminados`);
      } else {
        toast.error('Error al limpiar mensajes');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al limpiar mensajes');
    } finally {
      setCleaningMessages(false);
    }
  };

  // Función para diagnosticar mensajes no leídos fantasma
  const diagnosticarMensajes = async () => {
    try {
      setDiagnosticando(true);
      toast.info('🔍 Analizando mensajes no leídos...');

      const response = await axios.get(`${API}/admin/diagnostico-mensajes-no-leidos`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setDiagnosticoResult(response.data);
      
      if (response.data.usuarios_con_problemas?.length > 0) {
        toast.warning(`⚠️ ${response.data.usuarios_con_problemas.length} usuarios con mensajes fantasma`);
      } else {
        toast.success('✅ No se encontraron mensajes fantasma');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al diagnosticar');
    } finally {
      setDiagnosticando(false);
    }
  };

  // Función para resetear TODOS los contadores de chat
  const resetearContadoresChat = async () => {
    if (!window.confirm('⚠️ ACCIÓN NUCLEAR: Esto marcará TODOS los mensajes como leídos para TODOS los usuarios. ¿Continuar?')) {
      return;
    }

    try {
      setReseteandoContadores(true);
      toast.info('🔄 Reseteando contadores...');

      const response = await axios.post(`${API}/admin/resetear-contadores-chat`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const r = response.data.resultados;
        toast.success(`✅ Contadores reseteados:\n• ${r.mensajes_directos_eliminados} mensajes directos\n• ${r.conversaciones_reseteadas} conversaciones\n• ${r.chat_messages_marcados_leidos} chat messages\n• ${r.lecturas_canales_creadas} lecturas de canales`);
        setDiagnosticoResult(null); // Limpiar diagnóstico anterior
      } else {
        toast.error('Error al resetear contadores');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al resetear contadores');
    } finally {
      setReseteandoContadores(false);
    }
  };

  // 2. Borrar todos los datos
  const handleDeleteAllData = async (includeUsers = false) => {
    const warningMessage = includeUsers 
      ? '⚠️ PELIGRO EXTREMO: Esto borrará TODO incluyendo USUARIOS.\n\nEscribe "BORRAR TODO CON USUARIOS" para confirmar:'
      : '⚠️ PELIGRO: Esto borrará TODO (semestres, precios, clases, registraciones, etc.) EXCEPTO usuarios.\n\nEscribe "BORRAR TODO" para confirmar:';
    
    const confirmText = includeUsers ? 'BORRAR TODO CON USUARIOS' : 'BORRAR TODO';
    
    const confirmation = window.prompt(warningMessage);
    
    if (confirmation !== confirmText) {
      toast.error('Operación cancelada');
      return;
    }

    try {
      setDeletingAll(true);
      toast.info('Borrando todos los datos...');

      const response = await fetch(`${API}/admin/backup/delete-all-data?include_users=${includeUsers}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        
        if (includeUsers) {
          toast.warning('⚠️ Usuarios borrados. Deberás volver a iniciar sesión.', { duration: 5000 });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al borrar datos');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al borrar datos');
    } finally {
      setDeletingAll(false);
    }
  };

  // 3. Seleccionar archivo
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        setSelectedFile(file);
        toast.info(`Archivo seleccionado: ${file.name}`);
      } else {
        toast.error('Por favor selecciona un archivo JSON');
      }
    }
  };

  // 4. Importar backup
  const handleImportComplete = async () => {
    if (!selectedFile) {
      toast.error('Por favor selecciona un archivo primero');
      return;
    }

    if (!window.confirm('⚠️ ADVERTENCIA: Esto importará todos los datos del backup. ¿Estás seguro?')) {
      return;
    }

    try {
      setImporting(true);
      toast.info('Importando datos...', { duration: 5000 });

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API}/admin/backup/import-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        
        // Mostrar resultado detallado
        if (result.success) {
          toast.success(result.message, { duration: 6000 });
        } else {
          toast.warning(result.message, { duration: 8000 });
        }
        
        // Mostrar detalles si hay errores
        if (result.details && result.details.errors && result.details.errors.length > 0) {
          console.error('❌ Errores de importación:', result.details.errors);
          
          // Mostrar cada error
          result.details.errors.forEach((err, index) => {
            setTimeout(() => {
              toast.error(`Error en ${err.collection}: ${err.error}`, { 
                duration: 10000,
                description: err.details
              });
            }, (index + 1) * 1000);
          });
        }
        
        // Mostrar lo que se importó exitosamente
        if (result.details && result.details.imported && result.details.imported.length > 0) {
          console.log('✅ Colecciones importadas:', result.details.imported);
        }
        
        setSelectedFile(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al importar backup');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al importar backup: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  // 5. Limpiar datos de prueba (moved from AdminPagos)
  const limpiarDatosPrueba = () => {
    setIsDeleteConfirmOpen(true);
  };

  const confirmarLimpiarDatos = async () => {
    setIsDeleteConfirmOpen(false);
    
    try {
      setLoading(true);
      console.log('Enviando petición para eliminar datos de prueba...');
      
      const response = await axios.delete(`${API}/admin/registraciones-all`, {
        params: {
          confirm: 'DELETE_ALL'
        }
      });
      
      console.log('Respuesta del servidor:', response.data);
      
      if (response.data.deleted_count > 0) {
        toast.success(`✅ ${response.data.message} (${response.data.deleted_count} registros eliminados)`);
      } else {
        toast.info('ℹ️ No había datos de prueba para eliminar');
      }
    } catch (error) {
      console.error('Error al limpiar datos:', error);
      toast.error(error.response?.data?.detail || 'Error al limpiar datos de prueba');
    } finally {
      setLoading(false);
    }
  };

  const cancelarLimpiarDatos = () => {
    setIsDeleteConfirmOpen(false);
    toast.info('Operación cancelada por el usuario');
  };

  // 6. Reset completo de la aplicación (moved from AdminPagos)
  const resetCompleto = () => {
    setIsResetConfirmOpen(true);
  };

  const confirmarResetCompleto = async () => {
    setIsResetConfirmOpen(false);
    
    try {
      setLoading(true);
      console.log('Enviando petición para reset completo...');
      
      const response = await axios.delete(`${API}/admin/reset-application`, {
        params: {
          confirm: 'RESET_ALL_DATA'
        }
      });
      
      console.log('Respuesta del servidor:', response.data);
      
      const totalDeleted = Object.values(response.data.deleted_counts || {}).reduce((a, b) => a + b, 0);
      
      if (totalDeleted > 0) {
        toast.success(`✅ Sistema completamente reseteado (${totalDeleted} registros eliminados total)`);
      } else {
        toast.info('ℹ️ Sistema ya estaba limpio');
      }
    } catch (error) {
      console.error('Error al resetear sistema:', error);
      toast.error(error.response?.data?.detail || 'Error al resetear sistema completo');
    } finally {
      setLoading(false);
    }
  };

  const cancelarResetCompleto = () => {
    setIsResetConfirmOpen(false);
    toast.info('Reset completo cancelado');
  };

  // 7. Fix calificaciones estudiantes (moved from AdminPagos)
  const fixCalificaciones = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/fix/calificaciones-visibility`);
      toast.success('Fix aplicado correctamente');
    } catch (error) {
      toast.error('Error al aplicar fix');
    } finally {
      setLoading(false);
    }
  };

  // 8. Cargar lista de backups
  const loadBackups = async () => {
    try {
      setLoadingBackups(true);
      const response = await axios.get(`${API}/admin/backups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error cargando backups:', error);
      toast.error('Error al cargar lista de backups');
    } finally {
      setLoadingBackups(false);
    }
  };

  // 9. Descargar backup específico
  const downloadBackup = async (filename) => {
    try {
      toast.info('Descargando backup...');
      const response = await fetch(`${API}/admin/backups/download/${filename}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('✅ Backup descargado');
      } else {
        toast.error('Error al descargar backup');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al descargar backup');
    }
  };

  // 10. Eliminar backup
  const deleteBackup = async (filename) => {
    if (!window.confirm(`¿Estás seguro de eliminar el backup "${filename}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/backups/${filename}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Backup eliminado');
      loadBackups(); // Recargar lista
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar backup');
    }
  };

  // 11. Manejar selección de checkbox individual
  const handleSelectBackup = (filename) => {
    setSelectedBackups(prev => {
      if (prev.includes(filename)) {
        return prev.filter(f => f !== filename);
      } else {
        return [...prev, filename];
      }
    });
  };

  // 12. Manejar selección de todos
  const handleSelectAll = () => {
    if (selectedBackups.length === backups.length) {
      // Deseleccionar todos
      setSelectedBackups([]);
    } else {
      // Seleccionar todos
      setSelectedBackups(backups.map(b => b.filename));
    }
  };

  // 13. Eliminar backups seleccionados
  const deleteBulkBackups = async () => {
    if (selectedBackups.length === 0) {
      toast.error('No hay backups seleccionados');
      return;
    }

    const confirmMessage = `¿Estás seguro de eliminar ${selectedBackups.length} backup(s) seleccionado(s)?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingBulk(true);
      toast.info(`Eliminando ${selectedBackups.length} backup(s)...`);

      const response = await axios.post(`${API}/admin/backups/bulk-delete`, 
        selectedBackups,
        {
          headers: { 
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { deleted_files, protected_files, failed_files, message } = response.data;

      // Mostrar resultado
      if (deleted_files.length > 0) {
        toast.success(`✅ ${deleted_files.length} backup(s) eliminado(s)`);
      }
      if (protected_files.length > 0) {
        toast.warning(`⚠️ ${protected_files.length} backup(s) protegido(s) (más reciente no se puede eliminar)`);
      }
      if (failed_files.length > 0) {
        toast.error(`❌ ${failed_files.length} backup(s) fallaron`);
      }

      // Limpiar selección y recargar lista
      setSelectedBackups([]);
      loadBackups();

    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar backups');
    } finally {
      setDeletingBulk(false);
    }
  };

  // Cargar backups al montar el componente
  useEffect(() => {
    loadBackups();
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Database className="h-8 w-8 text-blue-600" />
          Gestión de Backups
        </h1>
        <p className="text-gray-600 mt-2">Exporta e importa datos completos entre ambientes</p>
      </div>

      {/* Tarjeta principal con 3 pasos */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          🔄 Transferencia de Datos: Preview → Producción
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* PASO 1: Exportar */}
          <div className="bg-white rounded-lg p-5 shadow-sm border-2 border-blue-300">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">1</span>
              <h3 className="font-semibold text-lg">Exportar desde Preview</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Descarga todos los datos (semestres, precios, usuarios, etc.) a tu PC</p>
            <button
              onClick={handleExportComplete}
              disabled={exporting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-50"
            >
              {exporting ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5" />
                  Descargar Backup
                </>
              )}
            </button>
            
            {/* BOTÓN DE IMPORTACIÓN FORZADA */}
            <div className="mt-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
              <h4 className="font-bold text-green-800 mb-2">🚀 Importación Rápida (Recomendado)</h4>
              <p className="text-sm text-gray-700 mb-3">
                Importa automáticamente todos los datos SIN necesidad de subir archivo. 
                Borra todo excepto tu usuario actual e importa 10 usuarios + 7 clases + 8 semestres.
              </p>
              <button
                onClick={async () => {
                  if (!window.confirm('¿Estás seguro? Esto borrará todos los datos excepto tu usuario e importará el backup completo.')) {
                    return;
                  }
                  
                  try {
                    setImporting(true);
                    toast.info('Importando backup... esto puede tardar 30 segundos');
                    
                    const response = await fetch(`${API}/admin/import-backup-force`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      }
                    });
                    
                    const data = await response.json();
                    
                    if (response.ok && data.success) {
                      toast.success(`✅ ${data.message}`);
                      setTimeout(() => {
                        window.location.reload();
                      }, 2000);
                    } else {
                      toast.error(data.detail || 'Error en la importación');
                    }
                  } catch (error) {
                    console.error('Error:', error);
                    toast.error('Error al importar backup');
                  } finally {
                    setImporting(false);
                  }
                }}
                disabled={importing}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition disabled:opacity-50 font-bold w-full justify-center"
              >
                {importing ? (
                  <>
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    Importando... (30 seg)
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    IMPORTAR AHORA (Un Click)
                  </>
                )}
              </button>
            </div>
          </div>

          {/* PASO 2: Borrar (OPCIONAL - el botón verde lo hace automáticamente) */}
          <div className="bg-white rounded-lg p-5 shadow-sm border-2 border-gray-300 opacity-60">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-gray-400 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">2</span>
              <h3 className="font-semibold text-lg text-gray-600">Limpiar Producción (OPCIONAL)</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">⚠️ Ya no es necesario - El botón verde lo hace automáticamente</p>
            <button
              onClick={() => handleDeleteAllData(false)}
              disabled={deletingAll}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-50 mb-2"
            >
              {deletingAll ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Borrando...
                </>
              ) : (
                <>
                  <Trash2 className="h-5 w-5" />
                  Borrar Todo (mantener usuarios)
                </>
              )}
            </button>
            <button
              onClick={() => handleDeleteAllData(true)}
              disabled={deletingAll}
              className="w-full bg-red-800 hover:bg-red-900 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-50 text-sm"
            >
              {deletingAll ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Borrando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Borrar Todo + Usuarios
                </>
              )}
            </button>
          </div>

          {/* PASO 3: Importar Manual desde tu PC - MÉTODO SEGURO */}
          <div className="bg-white rounded-lg p-5 shadow-sm border-2 border-blue-400">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">3</span>
              <h3 className="font-semibold text-lg text-blue-900">📁 Subir Backup desde tu PC (RECOMENDADO)</h3>
            </div>
            <p className="text-sm text-blue-700 font-medium mb-4">✅ Método más seguro: Sube el backup que tienes guardado en tu computadora</p>
            
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
              id="backup-file-input"
            />
            
            <label 
              htmlFor="backup-file-input" 
              className="block w-full bg-blue-100 hover:bg-blue-200 border-2 border-blue-400 text-blue-900 font-bold py-4 px-4 rounded-lg text-center cursor-pointer mb-3 transition text-lg"
            >
              <Upload className="h-5 w-5 inline mr-2" />
              {selectedFile ? `✅ ${selectedFile.name}` : '📂 Click aquí para Seleccionar tu Backup'}
            </label>
            
            <button
              onClick={handleImportComplete}
              disabled={!selectedFile || importing}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-4 rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed text-lg shadow-lg"
            >
              {importing ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5" />
                  Importar Backup
                </>
              )}
            </button>
          </div>

        </div>
      </div>

      {/* Herramientas Administrativas */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          🛠️ Herramientas Administrativas
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          
          {/* Limpiar Datos de Prueba */}
          <button
            onClick={limpiarDatosPrueba}
            disabled={loading}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-orange-300 hover:border-orange-400 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-orange-100 p-2 rounded-full">
                <i className="fas fa-trash-alt text-orange-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Limpiar Datos</h3>
            </div>
            <p className="text-sm text-gray-600">
              Elimina registros de inscripciones de prueba del sistema
            </p>
          </button>

          {/* Reset Completo */}
          <button
            onClick={resetCompleto}
            disabled={loading}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-red-400 hover:border-red-500 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-red-100 p-2 rounded-full">
                <i className="fas fa-nuclear text-red-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">RESET COMPLETO</h3>
            </div>
            <p className="text-sm text-gray-600">
              ⚠️ Elimina TODO excepto admin principal
            </p>
          </button>

          {/* Fix Calificaciones */}
          <button
            onClick={fixCalificaciones}
            disabled={loading}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-green-300 hover:border-green-400 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-green-100 p-2 rounded-full">
                <i className="fas fa-wrench text-green-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Fix Calificaciones</h3>
            </div>
            <p className="text-sm text-gray-600">
              Repara visualización de calificaciones de estudiantes
            </p>
          </button>

          {/* Corregir Canales de Chat */}
          <button
            onClick={async () => {
              try {
                toast.info('🔄 Corrigiendo canales de chat...');
                const response = await axios.post(`${API}/chat/canales/admin/fix-all`);
                if (response.data.success) {
                  toast.success(`✅ ${response.data.message}. Roles: ${response.data.updated_roles_permitidos}, Activo: ${response.data.updated_activo}`);
                } else {
                  toast.error('Error al corregir canales');
                }
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Error al corregir canales');
              }
            }}
            disabled={loading}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-orange-300 hover:border-orange-400 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-orange-100 p-2 rounded-full">
                <i className="fas fa-comments text-orange-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Corregir Canales Chat</h3>
            </div>
            <p className="text-sm text-gray-600">
              💬 Actualiza canales para notificaciones push
            </p>
          </button>

          {/* Limpiar Mensajes Huérfanos del Chat */}
          <button
            onClick={limpiarMensajesHuerfanos}
            disabled={cleaningMessages}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-blue-300 hover:border-blue-400 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-blue-100 p-2 rounded-full">
                <i className="fas fa-broom text-blue-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Limpiar Mensajes Chat</h3>
            </div>
            <p className="text-sm text-gray-600">
              {cleaningMessages ? '🧹 Limpiando...' : '🗑️ Elimina mensajes huérfanos del sistema viejo'}
            </p>
          </button>

          {/* Diagnosticar Mensajes No Leídos Fantasma */}
          <button
            onClick={diagnosticarMensajes}
            disabled={diagnosticando}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-cyan-300 hover:border-cyan-400 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-cyan-100 p-2 rounded-full">
                <Search className="h-4 w-4 text-cyan-600" />
              </div>
              <h3 className="font-semibold text-gray-800">Diagnosticar Chat</h3>
            </div>
            <p className="text-sm text-gray-600">
              {diagnosticando ? '🔍 Analizando...' : '🔍 Encuentra mensajes fantasma (1 sin leer)'}
            </p>
          </button>

          {/* Resetear TODOS los Contadores de Chat */}
          <button
            onClick={resetearContadoresChat}
            disabled={reseteandoContadores}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-rose-300 hover:border-rose-400 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-rose-100 p-2 rounded-full">
                <RotateCcw className="h-4 w-4 text-rose-600" />
              </div>
              <h3 className="font-semibold text-gray-800">Reset Contadores Chat</h3>
            </div>
            <p className="text-sm text-gray-600">
              {reseteandoContadores ? '🔄 Reseteando...' : '⚠️ Marca TODO como leído (Nuclear)'}
            </p>
          </button>

          {/* Backup Colecciones a MEGA */}
          <button
            onClick={async () => {
              setShowColeccionesConfig(!showColeccionesConfig);
              if (!showColeccionesConfig && coleccionesInfo.length === 0) {
                setLoadingColecciones(true);
                try {
                  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
                  const res = await axios.get(`${API}/admin/backup/colecciones-config`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  setColeccionesInfo(res.data.collections || []);
                } catch (err) {
                  toast.error('Error cargando colecciones');
                } finally {
                  setLoadingColecciones(false);
                }
              }
            }}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-teal-300 hover:border-teal-400 transition group"
            data-testid="btn-backup-colecciones-mega"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-teal-100 p-2 rounded-full">
                <i className="fas fa-cloud-upload-alt text-teal-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Backup Colecciones MEGA</h3>
            </div>
            <p className="text-sm text-gray-600">
              Sube cada colección como archivo separado a MEGA.nz (auto mensual día 1)
            </p>
          </button>

          {/* Fix Creadores de Canales */}
          <button
            onClick={async () => {
              if (!window.confirm('¿Corregir canales de chat?\n\nEsto agrega al creador como miembro en los canales donde falte, para que reciba notificaciones.')) return;
              try {
                const token = localStorage.getItem('token') || localStorage.getItem('access_token');
                const res = await axios.post(`${API}/chat/channels/fix-creators`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success(`${res.data.fixed} canales corregidos`);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Error corrigiendo canales');
              }
            }}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-amber-300 hover:border-amber-400 transition group"
            data-testid="btn-fix-creators"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-amber-100 p-2 rounded-full">
                <i className="fas fa-users-cog text-amber-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Fix Creadores Canales</h3>
            </div>
            <p className="text-sm text-gray-600">
              Agrega al creador como miembro en canales donde falte
            </p>
          </button>

          {/* Test Notificación Push */}
          <button
            onClick={async () => {
              const email = window.prompt('Email del usuario para enviar notificación de prueba:', '');
              if (!email) return;
              try {
                const token = localStorage.getItem('token') || localStorage.getItem('access_token');
                const res = await axios.post(`${API}/chat/test-push-notification`, 
                  { email_destino: email },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                const success = res.data.success;
                if (success) {
                  toast.success('Notificación enviada correctamente');
                } else {
                  toast.error('No se pudo enviar la notificación');
                }
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Error en test');
              }
            }}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-blue-300 hover:border-blue-400 transition group"
            data-testid="btn-test-push"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-blue-100 p-2 rounded-full">
                <i className="fas fa-bell text-blue-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Test Notificación Push</h3>
            </div>
            <p className="text-sm text-gray-600">
              Envía notificación de prueba a un usuario
            </p>
          </button>

          {/* Test Push Canal (Diagnóstico) */}
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem('token') || localStorage.getItem('access_token');
                // Obtener canales primero
                const chRes = await axios.get(`${API}/chat/channels`, { headers: { Authorization: `Bearer ${token}` } });
                const channels = chRes.data.channels || [];
                if (channels.length === 0) { toast.error('No hay canales disponibles'); return; }
                
                const options = channels.map((ch, i) => `${i+1}. ${ch.name} (${(ch.members||[]).length} miembros)`).join('\n');
                const selection = window.prompt(`Selecciona un canal (número):\n${options}`, '1');
                if (!selection) return;
                const idx = parseInt(selection) - 1;
                if (idx < 0 || idx >= channels.length) { toast.error('Selección inválida'); return; }
                
                const selectedChannel = channels[idx];
                toast.info(`Enviando test push a cada miembro de "${selectedChannel.name}"...`);
                
                const res = await axios.post(`${API}/chat/test-channel-push`,
                  { channel_id: selectedChannel.id },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                
                const data = res.data;
                const summary = data.summary || {};
                let detailMsg = `Canal: ${selectedChannel.name}\n\nOK: ${summary.ok || 0} | Fallidos: ${summary.failed || 0} | Saltados: ${summary.skipped || 0}\n\nDetalle por miembro:\n`;
                (data.results || []).forEach(r => {
                  detailMsg += `\n${r.status} - ${r.email || r.id} ${r.error ? '(' + JSON.stringify(r.error).substring(0,100) + ')' : ''}`;
                });
                alert(detailMsg);
                
                if (summary.ok > 0) toast.success(`${summary.ok} notificaciones enviadas`);
                else toast.error('Ninguna notificación fue enviada');
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Error en test de canal');
              }
            }}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-green-300 hover:border-green-400 transition group"
            data-testid="btn-test-channel-push"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-green-100 p-2 rounded-full">
                <i className="fas fa-bullhorn text-green-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Test Push Canal</h3>
            </div>
            <p className="text-sm text-gray-600">
              Diagnostica notificaciones por canal: envía test individual a cada miembro
            </p>
          </button>

          {/* Sincronizar Contadores de Email */}
          <button
            onClick={async () => {
              if (!window.confirm('¿Sincronizar contadores de emails para todos los usuarios?\n\nEsto recalculará los emails no leídos reales.')) return;
              try {
                const token = localStorage.getItem('token');
                toast.info('🔄 Sincronizando contadores de email...');
                const response = await fetch(`${API}/admin/sincronizar-contadores-email`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (data.success) {
                  toast.success(`✅ Sincronización completada: ${data.estadisticas.usuarios_procesados} usuarios procesados`);
                  alert(`✅ Sincronización completada!\n\n` +
                    `📊 Usuarios procesados: ${data.estadisticas.usuarios_procesados}\n` +
                    `📧 Usuarios con emails pendientes: ${data.estadisticas.usuarios_con_emails_pendientes}\n` +
                    `📬 Total emails en sistema: ${data.estadisticas.total_emails_en_sistema}`);
                } else {
                  toast.error('❌ Error: ' + (data.detail || 'Error desconocido'));
                }
              } catch (error) {
                toast.error('❌ Error: ' + error.message);
              }
            }}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-purple-300 hover:border-purple-400 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-purple-100 p-2 rounded-full">
                <i className="fas fa-sync-alt text-purple-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Sincronizar Emails</h3>
            </div>
            <p className="text-sm text-gray-600">
              📧 Recalcula contadores de emails no leídos para todos los usuarios
            </p>
          </button>

          {/* Limpiar Emails Fantasma */}
          <button
            onClick={async () => {
              if (!window.confirm('¿Limpiar emails fantasma y corregir inconsistencias?\n\nEsto puede tomar unos segundos.')) return;
              try {
                const token = localStorage.getItem('token');
                toast.info('🧹 Limpiando emails fantasma...');
                const response = await fetch(`${API}/admin/limpiar-emails-fantasma`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (data.success) {
                  toast.success(`✅ Limpieza completada: ${data.total_correcciones} correcciones`);
                  alert(`✅ Limpieza completada!\n\n` +
                    `🔧 Total correcciones: ${data.total_correcciones}\n` +
                    `📭 Emails sin destinatarios: ${data.correcciones.emails_sin_destinatarios}\n` +
                    `👤 Destinatarios inexistentes: ${data.correcciones.destinatarios_inexistentes_removidos}\n` +
                    `✅ Leído por corregidos: ${data.correcciones.leido_por_corregidos}\n` +
                    `🗑️ Eliminado por corregidos: ${data.correcciones.eliminado_por_corregidos}`);
                } else {
                  toast.error('❌ Error: ' + (data.detail || 'Error desconocido'));
                }
              } catch (error) {
                toast.error('❌ Error: ' + error.message);
              }
            }}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-pink-300 hover:border-pink-400 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-pink-100 p-2 rounded-full">
                <i className="fas fa-ghost text-pink-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Limpiar Emails Fantasma</h3>
            </div>
            <p className="text-sm text-gray-600">
              👻 Elimina emails con destinatarios inexistentes y corrige inconsistencias
            </p>
          </button>

          {/* Diagnosticar Usuario Específico */}
          <button
            onClick={async () => {
              const userEmail = window.prompt('Ingresa el EMAIL del usuario a diagnosticar:');
              if (!userEmail) return;
              
              try {
                const token = localStorage.getItem('token');
                toast.info(`🔍 Diagnosticando emails de ${userEmail}...`);
                const response = await fetch(`${API}/admin/diagnosticar-emails-usuario?user_email=${encodeURIComponent(userEmail)}`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                
                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                  toast.error('❌ El servidor no respondió correctamente. ¿Hiciste deploy?');
                  alert('❌ Error: Necesitas hacer deploy primero.');
                  return;
                }
                
                const data = await response.json();
                
                if (data.success) {
                  let mensaje = `✅ Diagnóstico para: ${data.usuario.nombre}\n\n`;
                  mensaje += `📊 ESTADÍSTICAS:\n`;
                  mensaje += `• Total como destinatario: ${data.estadisticas.total_como_destinatario}\n`;
                  mensaje += `• Emails visibles: ${data.estadisticas.emails_visibles}\n`;
                  mensaje += `• Emails no leídos: ${data.estadisticas.emails_no_leidos}\n`;
                  mensaje += `• Emails eliminados: ${data.estadisticas.emails_eliminados}\n`;
                  mensaje += `• Problemas encontrados: ${data.estadisticas.emails_con_problemas}\n`;
                  mensaje += `• Reparaciones aplicadas: ${data.estadisticas.reparaciones_aplicadas}\n`;
                  
                  if (data.emails_no_leidos_detalle && data.emails_no_leidos_detalle.length > 0) {
                    mensaje += `\n📧 EMAILS NO LEÍDOS:\n`;
                    data.emails_no_leidos_detalle.forEach((e, i) => {
                      mensaje += `${i+1}. ${e.asunto}\n`;
                    });
                  }
                  
                  toast.success(`✅ Diagnóstico completado`);
                  alert(mensaje);
                } else {
                  toast.error('❌ Error: ' + (data.detail || 'Error desconocido'));
                }
              } catch (error) {
                toast.error('❌ Error: ' + error.message);
              }
            }}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-cyan-300 hover:border-cyan-400 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-cyan-100 p-2 rounded-full">
                <i className="fas fa-user-md text-cyan-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Diagnosticar Usuario</h3>
            </div>
            <p className="text-sm text-gray-600">
              🔍 Diagnostica y repara emails de un usuario específico
            </p>
          </button>

          {/* DEBUG PROFUNDO - Para encontrar el email problemático */}
          <button
            onClick={async () => {
              const userEmail = window.prompt('EMAIL del usuario con error 500:');
              if (!userEmail) return;
              
              try {
                const token = localStorage.getItem('token');
                toast.info(`🔬 Debug profundo de ${userEmail}...`);
                const response = await fetch(`${API}/admin/debug-emails-usuario?user_email=${encodeURIComponent(userEmail)}`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                
                const data = await response.json();
                
                if (data.success) {
                  let mensaje = `🔬 DEBUG PROFUNDO\n`;
                  mensaje += `Usuario: ${data.usuario}\n`;
                  mensaje += `Total emails: ${data.total_emails}\n`;
                  mensaje += `✅ OK: ${data.emails_ok}\n`;
                  mensaje += `❌ Errores: ${data.emails_error}\n\n`;
                  
                  if (data.errores_detalle && data.errores_detalle.length > 0) {
                    mensaje += `⚠️ EMAILS CON ERROR:\n`;
                    data.errores_detalle.forEach((e, i) => {
                      mensaje += `\n${i+1}. ID: ${e.id}\n`;
                      mensaje += `   Asunto: ${e.asunto}\n`;
                      mensaje += `   Error: ${e.error}\n`;
                      mensaje += `   Tipo: ${e.error_tipo}\n`;
                    });
                  } else {
                    mensaje += `✅ Todos los emails pasaron el debug.\n`;
                    mensaje += `El error puede estar en otro lugar.`;
                  }
                  
                  console.log('Debug completo:', data);
                  toast.success(`Debug completado - Ver consola para detalles`);
                  alert(mensaje);
                } else {
                  toast.error('❌ ' + (data.detail || 'Error'));
                }
              } catch (error) {
                toast.error('❌ ' + error.message);
                alert('Error: ' + error.message);
              }
            }}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-red-300 hover:border-red-400 transition group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-red-100 p-2 rounded-full">
                <i className="fas fa-bug text-red-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Debug Profundo</h3>
            </div>
            <p className="text-sm text-gray-600">
              🔬 Encuentra el email exacto que causa error 500
            </p>
          </button>

          {/* Limpiar Calificaciones Formulario Registro */}
          <button
            onClick={async () => {
              if (!window.confirm('¿Eliminar calificaciones falsas generadas por el formulario de registro?\n\nEste formulario no es un examen, no debe tener calificaciones.')) return;
              try {
                const token = localStorage.getItem('token');
                toast.info('Limpiando calificaciones del formulario de registro...');
                const response = await fetch(`${API}/admin/limpiar-calificaciones-formulario-registro`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (data.success) {
                  toast.success(`Eliminadas ${data.eliminadas} calificaciones falsas`);
                  alert(`Limpieza completada!\n\nCalificaciones falsas eliminadas: ${data.eliminadas}\n\nEstas calificaciones fueron generadas incorrectamente cuando los estudiantes llenaron el formulario de registro.`);
                } else {
                  toast.error('Error: ' + (data.detail || 'Error desconocido'));
                }
              } catch (error) {
                toast.error('Error: ' + error.message);
              }
            }}
            disabled={loading}
            className="bg-white rounded-lg p-5 shadow-sm border-2 border-amber-300 hover:border-amber-400 transition group"
            data-testid="btn-limpiar-calificaciones-registro"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-amber-100 p-2 rounded-full">
                <i className="fas fa-eraser text-amber-600"></i>
              </div>
              <h3 className="font-semibold text-gray-800">Limpiar Calificaciones Registro</h3>
            </div>
            <p className="text-sm text-gray-600">
              Elimina calificaciones falsas del formulario de registro (no es examen)
            </p>
          </button>
          
        </div>
      </div>

      {/* Panel de Configuración de Backup por Colecciones */}
      {showColeccionesConfig && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-2 border-teal-200" data-testid="colecciones-config-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <i className="fas fa-cloud-upload-alt text-teal-600"></i>
              Backup por Colecciones a MEGA.nz
            </h3>
            <button onClick={() => setShowColeccionesConfig(false)} className="text-gray-400 hover:text-gray-600">
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mb-3">
            Seleccioná las colecciones que querés respaldar individualmente. Si no seleccionás ninguna, se respaldan todas.
            Los archivos se sobrescriben cada vez (no se duplican).
          </p>

          {loadingColecciones ? (
            <div className="text-center py-8 text-gray-500"><i className="fas fa-spinner fa-spin mr-2"></i>Cargando colecciones...</div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <button
                  onClick={() => setColeccionesInfo(prev => prev.map(c => ({...c, selected: true})))}
                  className="text-xs px-3 py-1 bg-teal-100 text-teal-700 rounded-full hover:bg-teal-200 transition"
                >Seleccionar todas</button>
                <button
                  onClick={() => setColeccionesInfo(prev => prev.map(c => ({...c, selected: false})))}
                  className="text-xs px-3 py-1 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition"
                >Deseleccionar todas</button>
                <span className="text-xs text-gray-500">
                  {coleccionesInfo.filter(c => c.selected).length} de {coleccionesInfo.length} seleccionadas
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-72 overflow-y-auto border rounded-lg p-3 mb-4">
                {coleccionesInfo.map((col) => (
                  <label key={col.name} className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm transition ${col.selected ? 'bg-teal-50 border border-teal-200' : 'hover:bg-gray-50'}`}>
                    <input
                      type="checkbox"
                      checked={col.selected}
                      onChange={() => setColeccionesInfo(prev => prev.map(c => c.name === col.name ? {...c, selected: !c.selected} : c))}
                      className="rounded text-teal-600"
                    />
                    <span className="truncate font-medium">{col.name}</span>
                    <span className="text-xs text-gray-400 ml-auto">{col.count}</span>
                  </label>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
                      const selected = coleccionesInfo.filter(c => c.selected).map(c => c.name);
                      await axios.post(`${API}/admin/backup/colecciones-config`, selected, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success(`Configuración guardada: ${selected.length} colecciones`);
                    } catch (err) {
                      toast.error('Error guardando configuración');
                    }
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition"
                  data-testid="btn-guardar-colecciones-config"
                >
                  <i className="fas fa-save mr-1"></i> Guardar Selección
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('¿Iniciar backup ahora? Se respaldarán las colecciones seleccionadas a MEGA.nz (tarda unos minutos).')) return;
                    setBackupColecciones(true);
                    try {
                      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
                      // Primero guardar la selección
                      const selected = coleccionesInfo.filter(c => c.selected).map(c => c.name);
                      await axios.post(`${API}/admin/backup/colecciones-config`, selected, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      // Luego ejecutar
                      const res = await axios.post(`${API}/admin/backup/colecciones-mega`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success(res.data.message || 'Backup iniciado');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Error al iniciar backup');
                    } finally {
                      setBackupColecciones(false);
                    }
                  }}
                  disabled={backupColecciones}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition disabled:opacity-50"
                  data-testid="btn-ejecutar-backup-colecciones"
                >
                  {backupColecciones ? (
                    <><i className="fas fa-spinner fa-spin mr-1"></i> Iniciando...</>
                  ) : (
                    <><i className="fas fa-cloud-upload-alt mr-1"></i> Ejecutar Backup Ahora</>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {diagnosticoResult && diagnosticoResult.usuarios_con_problemas?.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-cyan-600" />
            Diagnóstico de Mensajes Fantasma
            <span className="text-sm font-normal text-red-600">
              ({diagnosticoResult.usuarios_con_problemas.length} usuarios afectados)
            </span>
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left">Usuario</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-center">Rol</th>
                  <th className="px-3 py-2 text-center">Msg Directos</th>
                  <th className="px-3 py-2 text-center">Conversaciones</th>
                  <th className="px-3 py-2 text-center">Chat Msgs</th>
                  <th className="px-3 py-2 text-center">Canales</th>
                  <th className="px-3 py-2 text-center font-bold">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {diagnosticoResult.usuarios_con_problemas.slice(0, 20).map((u, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2">{u.usuario}</td>
                    <td className="px-3 py-2 text-gray-600">{u.email}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${
                        u.rol === 'admin' ? 'bg-red-100 text-red-700' :
                        u.rol === 'profesor' ? 'bg-blue-100 text-blue-700' :
                        u.rol === 'estudiante' ? 'bg-green-100 text-green-700' :
                        u.rol === 'pastor' ? 'bg-purple-100 text-purple-700' :
                        u.rol === 'graduado' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {u.rol}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">{u.mensajes_directos || 0}</td>
                    <td className="px-3 py-2 text-center">{u.chat_conversations || 0}</td>
                    <td className="px-3 py-2 text-center">{u.chat_messages || 0}</td>
                    <td className="px-3 py-2 text-center">
                      {u.canales || 0}
                      {u.canales_detalle?.length > 0 && (
                        <span className="ml-1 text-xs text-gray-500">
                          ({u.canales_detalle.map(c => c.canal_id).join(', ')})
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center font-bold text-red-600">{u.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              💡 Si ves mensajes fantasma, usa el botón <strong>"Reset Contadores Chat"</strong> para limpiarlos.
            </p>
            <button
              onClick={() => setDiagnosticoResult(null)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cerrar diagnóstico
            </button>
          </div>
        </div>
      )}

      {/* Instrucciones */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-semibold text-yellow-800 mb-2">📋 Instrucciones:</h3>
        <ol className="list-decimal list-inside space-y-1 text-sm text-yellow-700">
          <li>En <strong>Preview</strong>: Haz clic en &quot;Descargar Backup&quot; (Paso 1)</li>
          <li>Haz <strong>Deploy</strong> de tu código a producción</li>
          <li>En <strong>Producción</strong>: Haz clic en &quot;Borrar Todo&quot; (Paso 2)</li>
          <li>En <strong>Producción</strong>: Selecciona el archivo y haz clic en &quot;Importar Backup&quot; (Paso 3)</li>
          <li>¡Listo! Todos tus datos de Preview ahora están en Producción</li>
        </ol>
      </div>

      {/* Lista de Backups Automáticos */}
      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Database className="h-6 w-6 text-purple-600" />
            Backups Automáticos Diarios
          </h2>
          <div className="flex gap-2">
            {selectedBackups.length > 0 && (
              <button
                onClick={deleteBulkBackups}
                disabled={deletingBulk}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition disabled:opacity-50 font-semibold"
              >
                {deletingBulk ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Eliminar Seleccionados ({selectedBackups.length})
                  </>
                )}
              </button>
            )}
            <button
              onClick={loadBackups}
              disabled={loadingBackups}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition"
            >
              <RefreshCw className={`h-4 w-4 ${loadingBackups ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          </div>
        </div>
        
        <p className="text-gray-600 mb-4">
          Los backups automáticos se crean diariamente a las 2:00 AM. Se mantienen los últimos 7 backups.
        </p>

        {loadingBackups ? (
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-500 mt-2">Cargando backups...</p>
          </div>
        ) : backups.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Database className="h-12 w-12 mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">No hay backups disponibles</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Checkbox "Seleccionar Todo" */}
            {backups.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200 mb-3">
                <input
                  type="checkbox"
                  checked={selectedBackups.length === backups.length && backups.length > 0}
                  onChange={handleSelectAll}
                  className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500 cursor-pointer"
                  id="select-all-backups"
                />
                <label htmlFor="select-all-backups" className="font-semibold text-purple-900 cursor-pointer select-none">
                  {selectedBackups.length === backups.length && backups.length > 0
                    ? `✓ Todos seleccionados (${backups.length})`
                    : `Seleccionar todos (${backups.length} backups)`}
                </label>
              </div>
            )}

            {/* Lista de backups */}
            {backups.map((backup) => (
              <div
                key={backup.filename}
                className={`flex items-center justify-between p-4 rounded-lg border transition ${
                  selectedBackups.includes(backup.filename)
                    ? 'bg-purple-50 border-purple-300 shadow-sm'
                    : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                }`}
              >
                {/* Checkbox de selección */}
                <div className="flex items-center gap-3 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedBackups.includes(backup.filename)}
                    onChange={() => handleSelectBackup(backup.filename)}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500 cursor-pointer"
                    id={`backup-${backup.filename}`}
                  />
                  <Database className="h-5 w-5 text-gray-500" />
                  <div>
                    <label 
                      htmlFor={`backup-${backup.filename}`}
                      className="font-medium text-gray-800 cursor-pointer select-none"
                    >
                      {backup.filename}
                    </label>
                    <div className="flex gap-3 text-sm text-gray-500">
                      <span>{backup.formatted_time}</span>
                      <span>•</span>
                      <span>{backup.size_mb} MB</span>
                      <span>•</span>
                      <span className={`font-semibold ${
                        backup.type === 'auto' ? 'text-blue-600' : 'text-green-600'
                      }`}>
                        {backup.type === 'auto' ? '🤖 Automático' : '👤 Manual'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => downloadBackup(backup.filename)}
                    className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                    title="Descargar backup"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteBackup(backup.filename)}
                    className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                    title="Eliminar backup"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de confirmación para eliminar datos de prueba */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Confirmar Eliminación de Datos
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="flex items-start space-x-3">
                <i className="fas fa-trash-alt text-red-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-red-800 mb-2">
                    ¿Estás seguro de eliminar TODOS los datos de prueba?
                  </h4>
                  <p className="text-sm text-red-700">
                    Esta acción eliminará permanentemente:
                  </p>
                  <ul className="text-sm text-red-700 mt-2 ml-4 space-y-1">
                    <li>• Todos los registros de inscripciones de prueba</li>
                    <li>• Información de pagos de prueba</li>
                    <li>• Comprobantes asociados</li>
                  </ul>
                  <p className="text-sm font-semibold text-red-800 mt-3">
                    ⚠️ Esta acción NO se puede deshacer
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-2 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={cancelarLimpiarDatos}
              disabled={loading}
            >
              <i className="fas fa-times mr-2"></i>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={confirmarLimpiarDatos}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Eliminando...
                </>
              ) : (
                <>
                  <i className="fas fa-trash-alt mr-2"></i>
                  Sí, Eliminar Todo
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmación para RESET COMPLETO */}
      <Dialog open={isResetConfirmOpen} onOpenChange={setIsResetConfirmOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-700">
              <i className="fas fa-nuclear mr-2"></i>
              ⚠️ RESET COMPLETO DEL SISTEMA
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-100 p-4 rounded-lg border-2 border-red-300">
              <div className="flex items-start space-x-3">
                <i className="fas fa-exclamation-triangle text-red-600 text-xl mt-1"></i>
                <div>
                  <h4 className="font-bold text-red-900 mb-3">
                    🚨 ADVERTENCIA CRÍTICA: Esta acción eliminará TODO
                  </h4>
                  <p className="text-sm text-red-800 mb-3">
                    Esta acción eliminará COMPLETAMENTE:
                  </p>
                  <ul className="text-sm text-red-800 ml-4 space-y-1">
                    <li>• 👥 TODOS los usuarios (excepto admin@instituto.uy)</li>
                    <li>• 📚 TODAS las clases creadas</li>
                    <li>• 🎓 TODAS las registraciones de estudiantes</li>
                    <li>• 📝 TODOS los formularios y respuestas</li>
                    <li>• 📊 TODAS las calificaciones</li>
                    <li>• 📅 TODAS las asistencias</li>
                    <li>• 💰 TODOS los registros de pagos</li>
                  </ul>
                  <div className="mt-4 p-3 bg-red-200 rounded border border-red-400">
                    <p className="text-sm font-bold text-red-900">
                      ⛔ SOLO quedará el administrador principal
                    </p>
                    <p className="text-sm font-bold text-red-900">
                      ⛔ Esta acción NO se puede deshacer
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-2 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={cancelarResetCompleto}
              disabled={loading}
            >
              <i className="fas fa-shield mr-2"></i>
              Cancelar (Seguro)
            </Button>
            <Button
              variant="destructive"
              className="flex-1 bg-red-700 hover:bg-red-800"
              onClick={confirmarResetCompleto}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Reseteando...
                </>
              ) : (
                <>
                  <i className="fas fa-nuclear mr-2"></i>
                  SÍ, RESET COMPLETO
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionBackupsFull;
